import * as React from 'react';
import { RatingsInput } from '..';
import { RatingsInputProps } from './types';
import { storiesOf } from '@storybook/react';
import { VisualTestContainer } from '../../test/visual/VisualTestContainer';
import { classes } from './RatingsInput.visual.st.css';
import { ErrorAppearance } from '../internal/ErrorMessageWrapper';

const Comp = (props) => (
  <VisualTestContainer>
    <RatingsInput {...props} />
  </VisualTestContainer>
);

const RatingsInputFocused: React.FC<RatingsInputProps> = (props) => {
  const ref = React.useRef(null);

  React.useEffect(() => {
    ref.current.focus();
  }, []);

  return (
    <VisualTestContainer>
      <RatingsInput {...props} ref={ref} />
    </VisualTestContainer>
  );
};

const commonProps = {
  getReactionLabel: (value) => {
    const labelMap = {
      1: 'Hmm',
      2: 'Ok',
      3: 'Good',
      4: 'Great',
      5: 'Amazing',
    };
    return labelMap[value];
  },
};

const tests: {
  describe: string;
  its: {
    it: string;
    props: Partial<RatingsInputProps>;
    Component?: React.FC;
    style?: Record<string, string>;
  }[];
}[] = [
  {
    describe: 'basic',
    its: [
      {
        it: 'default props',
        props: {},
      },
      {
        it: 'disabled',
        props: {
          label: 'Rate us',
          disabled: true,
        },
      },
      {
        it: 'with error message',
        props: {
          label: 'Rate us',
          value: 1,
          errorMessage: 'Field is required',
        },
      },
      {
        it: 'with error message / ErrorAppearance - BackgroundAndBorder',
        props: {
          errorAppearance: ErrorAppearance.BackgroundAndBorder,
          label: 'Rate us',
          value: 1,
          errorMessage: 'Field is required',
        },
      },
      {
        it: 'with label rendered at the top',
        props: {
          label: 'Rate us',
          value: 1,
        },
      },
      {
        it: 'with label and reaction rendered aside',
        props: {
          label: 'Rate us',
          value: 1,
          labelLayout: 'labelAside',
        },
      },
      {
        it: 'with label at the top and error message bellow',
        props: {
          label: 'Rate us',
          value: 2,
          labelLayout: 'labelAside',
          errorMessage: 'Field is required',
        },
      },
      {
        it: 'with label at the top and error message bellow / ErrorAppearance - BackgroundAndBorder',
        props: {
          errorAppearance: ErrorAppearance.BackgroundAndBorder,
          label: 'Rate us',
          value: 2,
          labelLayout: 'labelAside',
          errorMessage: 'Field is required',
        },
      },
      {
        it: 'with large size and label on top',
        props: {
          label: 'Rate us',
          size: 'large',
          value: 4,
        },
      },
      {
        it: 'with large size and label aside',
        props: {
          label: 'Rate us',
          size: 'large',
          labelLayout: 'labelAside',
          value: 4,
        },
      },
      {
        it: 'with showReactionLabel true',
        props: {
          value: 4,
          showReactionLabel: true,
        },
      },
      {
        it: 'with showReactionLabel false',
        props: {
          value: 4,
          showReactionLabel: false,
        },
      },
      {
        it: 'focused small',
        props: {
          value: 4,
          showReactionLabel: false,
          size: 'small',
        },
        Component: RatingsInputFocused,
      },
      {
        it: 'focused large',
        props: {
          value: 4,
          showReactionLabel: false,
          size: 'large',
        },
        Component: RatingsInputFocused,
      },
    ],
  },
  {
    describe: 'style overrides',
    its: [
      {
        it: 'label font',
        props: {
          className: classes.labelFont,
          label: 'test label',
        },
      },
      {
        it: 'label color',
        props: {
          className: classes.labelColor,
          label: 'test label',
        },
      },
      {
        it: 'icon color',
        props: {
          className: classes.iconColor,
          value: 1,
        },
      },
      {
        it: 'reaction label font',
        props: {
          className: classes.reactionLabelFontFamily,
          labelLayout: 'labelAside',
          value: 1,
        },
      },
      {
        it: 'reaction label color',
        props: {
          className: classes.reactionLabelColor,
          labelLayout: 'labelAside',
          value: 1,
        },
      },
      {
        it: 'error color',
        props: {
          className: classes.errorColor,
          value: 1,
          errorMessage: 'Field is required',
        },
      },
    ],
  },
  {
    describe: 'wst theming',
    its: [
      {
        it: 'default',
        props: {
          label: 'test label',
        },
        style: {
          '--wst-paragraph-2-color-rgb': '0,255,100',
          '--wst-paragraph-2-font-family': 'Libre Baskerville',
          '--wst-paragraph-2-font-size': '20px',
          '--wst-links-and-actions-color-rgb': '0,10,255',
          '--wst-paragraph-2-font-weight': 'bold',
          '--wst-paragraph-2-font-style': 'italic',
          '--wst-paragraph-2-font-variant': 'small-caps',
        },
      },
      {
        it: 'error state',
        props: {
          errorAppearance: ErrorAppearance.BackgroundAndBorder,
          errorMessage: 'error',
          value: 3,
        },
        style: {
          '--wst-system-error-color-rgb': '0,140,140',
        },
      },
      {
        it: 'disabled  state',
        props: {
          disabled: true,
          value: 3,
        },
        style: {
          '--wst-shade-3-color-rgb': '140,20,40',
        },
      },
    ],
  },
];

tests.forEach(({ describe, its }) => {
  its.forEach(({ it, props, Component = Comp, style }) => {
    storiesOf(`RatingsInput/${describe}`, module).add(it, () => (
      <div style={style}>
        <Component {...commonProps} {...props} />
      </div>
    ));
  });
});
